import { useDispatch, useSelector } from 'react-redux';
import { Modal, Stack, Text, Button, TextInput, Textarea, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { getInputStyles, getTextareaStyles } from '../reservation/reservation.helper';
import { sendEmail } from '../../../redux/reducers/publicSlice';
import { selectEmailLoading, selectReservationContent } from '../../../redux/selectors/publicSelector';
import colors from '../../../utils/styles';

interface CruiseShipModalProps {
    opened: boolean;
    onClose: () => void;
}

const CruiseShipModal = ({ opened, onClose }: CruiseShipModalProps) => {
    const dispatch = useDispatch();
    const reservation = useSelector(selectReservationContent);
    const emailLoading = useSelector(selectEmailLoading);
    const isMobile = useMediaQuery('(max-width: 600px)');

    const form = useForm({
        initialValues: {
            name: '',
            email: '',
            message: ''
        },
        validate: {
            name: (value) => value.length > 0 ? null : 'Name is required',
            email: (value) => value.length > 0 ? null : 'Email is required',
            message: (value) => value.length > 0 ? null : 'Message is required',
        }
    });

    const handleSendEmail = () => {
        dispatch(sendEmail({...form.values as any, subject: 'Cruise Ship Booking'}));
        setTimeout(() => {
            onClose();
            form.reset();
        }, 1000);
    };

    const handleClose = () => {
        onClose();
        form.reset();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            size={isMobile ? 'auto' : 'xl'}
            radius="md"
            withCloseButton
            title={reservation.cruiseShipPassengersTour.title}
            yOffset={isMobile ? '10vh' : '120px'}
            zIndex={1000}
            styles={{
                content: {
                    backgroundColor: colors.appLightBlue,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                },
                header: {
                    backgroundColor: colors.appLightBlue,
                },
                body: {
                    padding: 20,
                    color: 'white',
                },
                title: {
                    color: colors.appYellow,
                    fontSize: 'clamp(1.6rem, 2.8vw, 2rem)',
                    fontWeight: 700,
                    fontFamily: "Playfair Display",
                    textAlign: 'center',
                },
            }}>
            <Stack gap="lg">
                {/* Tour Description */}
                <Stack gap="md">
                    <Text fw={700} style={{ textAlign: 'center', color: colors.appYellow, fontSize: 'clamp(0.85rem, 2.3vw, 1.2rem)' }}>
                        {reservation.cruiseShipPassengersTour.subtitle}
                    </Text>

                    <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        {reservation.cruiseShipPassengersTour.description}
                    </Text>

                    <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        {reservation.cruiseShipPassengersTour.details.journey}
                    </Text>

                    <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        {reservation.cruiseShipPassengersTour.details.cookingExperience}
                    </Text>

                    <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        {reservation.cruiseShipPassengersTour.details.feast}
                    </Text>

                    <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        {reservation.cruiseShipPassengersTour.details.entertainment}
                    </Text>

                    <Text fw={700} style={{ textAlign: 'center', fontSize: 'clamp(0.9rem, 2.6vw, 1.3rem)' }}>
                        Smash plates and shout OPA!
                    </Text>

                    <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        {reservation.cruiseShipPassengersTour.details.platesAndFortune}
                    </Text>

                    <Stack gap="xs" mt="md">
                        <Text fw={700} size="lg" style={{ fontSize: 'clamp(0.9rem, 2.6vw, 1.3rem)' }}>
                            {reservation.cruiseShipPassengersTour.tourDetails.title}
                        </Text>
                        <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>{reservation.cruiseShipPassengersTour.tourDetails.duration}</Text>
                        <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>{reservation.cruiseShipPassengersTour.tourDetails.groupSize}</Text>
                        <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>{reservation.cruiseShipPassengersTour.tourDetails.includes}</Text>
                        <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>{reservation.cruiseShipPassengersTour.tourDetails.meetingPoint}</Text>
                        <Text style={{ fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>{reservation.cruiseShipPassengersTour.tourDetails.dropOff}</Text>
                        <Text fw={700} mt="xs" style={{ fontSize: 'clamp(0.85rem, 2.4vw, 1.2rem)' }}>{reservation.cruiseShipPassengersTour.tourDetails.price}</Text>
                    </Stack>
                </Stack>

                {/* Contact Form */}
                <Stack gap="sm" mt="lg" style={{ borderTop: `2px solid ${colors.appYellow}`, paddingTop: 20 }}>
                    <Text fw={700} size="lg" style={{ textAlign: 'center', color: colors.appYellow, fontSize: 'clamp(0.9rem, 2.6vw, 1.3rem)' }}>
                        Book Now - Contact Us
                    </Text>

                    <Text style={{ textAlign: 'center', fontSize: 'clamp(0.75rem, 2.3vw, 1.15rem)' }}>
                        Fill out the form below and we&apos;ll get back to you!
                    </Text>

                    <TextInput
                        label="Name"
                        styles={getInputStyles(form.errors.name) as any}
                        placeholder="Your name"
                        {...form.getInputProps('name')}
                        variant="unstyled"
                        radius={0}
                    />

                    <TextInput
                        label="Email"
                        styles={getInputStyles(form.errors.email) as any}
                        placeholder="Your email"
                        {...form.getInputProps('email')}
                        variant="unstyled"
                        radius={0}
                    />

                    <Textarea
                        label="Message"
                        styles={getTextareaStyles(form.errors.message, 100) as any}
                        placeholder="Tell us about your cruise schedule and group size..."
                        {...form.getInputProps('message')}
                        variant="unstyled"
                        radius={0}
                        minRows={4}
                    />

                    <Button
                        disabled={emailLoading || !form.isValid()}
                        mx="auto"
                        style={{ backgroundColor: colors.appPink, width: '35%' }}
                        onClick={handleSendEmail}
                        loading={emailLoading}>
                        Send Email
                    </Button>
                </Stack>

                {/* Close Button for Mobile */}
                {isMobile && (
                    <Group style={{ position: 'sticky', bottom: 0, width: '100%' }}>
                        <Button mx="auto" size="xs" mt={10} radius="lg" color="grey" onClick={handleClose}>
                            Close
                        </Button>
                    </Group>
                )}
            </Stack>
        </Modal>
    );
};

export default CruiseShipModal;
